import React from "react";
import { ScoreLabel } from "./ScoreLabel";
import { AppleRing } from "./AppleRing";
import { useWorldToScreen } from "../hooks/useWorldToScreen";
import { SHOW_DEBUG } from "../config";
import { CategoryData } from "../config/types";
import { useHintVisibility } from "../hooks/useHintVisibility";
import { BudgetHealthBar } from "./BudgetHealthBar";
import { useGameState } from "../providers/GameStateProvider";
import { BudgetPercentages } from "../utils/budgetCalculations";
import { Typography } from "@mui/material";

interface CatBoxProps {
  category: CategoryData;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  adultMode: boolean;
  className?: string;
  /**
   * When true, this cat has been selected (donor) in transfer mode.
   * Used to highlight the AppleRing.
   */
  selected?: boolean;
  catIndex?: number;
  /** budget percentages for Needs, Wants, and Savings */
  budgetPercentages?: BudgetPercentages;
}

export const CatBox: React.FC<CatBoxProps> = ({
  category,
  position,
  adultMode,
  className,
  selected = false,
  catIndex,
  budgetPercentages,
}) => {
  const { showNeedsKibbles, showBudgetLabels, showHasKibbles } =
    useHintVisibility();
  const { currentLevel } = useGameState();

  // Determine if this is a YIC level (nickname starts with 'yic-')
  const isYICLevel = currentLevel?.nickname?.startsWith("yic-") ?? false;
  
  // Show hints for all levels now, but hide BudgetHealthBar hint for:
  // 1. "New Car" in Liz's level
  // 2. Any category with savings = true
  const isLizLevel = currentLevel?.nickname === "yic-liz";
  const isNewCarCategory = category.name === "New Car";
  const isSavingsCategory = category.savings === true;
  const showHint = !(isLizLevel && isNewCarCategory) && !isSavingsCategory;

  // Calculate internal layout - standardized for all cats
  const { x, y, width, height } = position;
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Calculate actual cat position based on current positioning system
  // BudgetSceneReact positions cats at center of cells, not with offset
  const CAT_ACTUAL_Y = centerY; // In current system, cats are at cell center

  // The ring fully encompasses the cat
  const sizeBase = Math.min(width, height, 300); // Clamp visual elements to 300px square
  const ringRadius = sizeBase * 0.42;

  // Position label within the grid cell, top-center for category name
  const LABEL_OFFSET = 8;
  const LABEL_WORLD_X = centerX; // using center now for name top-center previously adjusted? Actually we updated anchor center
  const LABEL_WORLD_Y = y + LABEL_OFFSET; // top edge of cell plus padding

  // Font size slightly larger for prominence
  const nameFont = Math.max(sizeBase * 0.1, 12);

  // Bottom info label position & font - positioned at bottom of box
  const INFO_OFFSET = 8;
  const INFO_WORLD_X = centerX;
  const INFO_WORLD_Y = y + height - INFO_OFFSET; // bottom edge of cell minus padding
  const infoFont = Math.max(sizeBase * 0.07, 10);

  // -------- Vertical Budget Bar calculations -----------------------------
  const maxValue = Math.max(category.budgeted, category.spent, 1); // avoid div/0
  const barValue = category.budgeted;
  const spentMark = {
    value: category.spent,
    label: adultMode
      ? `$${category.spent.toLocaleString()}`
      : `${category.spent}`,
  };

  // Bar color: red if deficit else primary
  const barColor = category.spent > category.budgeted ? "error" : "primary";

  // -------- Info Label Grid Component -----------------------------
  const InfoLabelGrid: React.FC<{ adultMode: boolean }> = ({ adultMode }) => {
    const screenPos = useWorldToScreen({ x: INFO_WORLD_X, y: INFO_WORLD_Y });

    // Determine what to show based on hint visibility settings
    const showNeeds = showNeedsKibbles && category.spent > category.budgeted;
    const showHas = showHasKibbles && category.budgeted > category.spent;
    const showBudget = showBudgetLabels;

    // Calculate deficit/surplus
    const deficit = category.spent - category.budgeted;
    const surplus = category.budgeted - category.spent;

    // Only render if there's something to show
    if (!showNeeds && !showHas && !showBudget) {
      return null;
    }

    return (
      <div
        style={{
          position: "absolute",
          left: screenPos.left,
          top: screenPos.top,
          transform: "translate(-50%, -50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2px",
          zIndex: 10,
        }}
      >
        {/* Needs kibbles (red) */}
        {showNeeds && (
          <ScoreLabel
            world={{ x: INFO_WORLD_X, y: INFO_WORLD_Y }}
            text={adultMode ? `$${deficit}` : `${deficit}`}
            style={{
              fontSize: `${infoFont}px`,
              fontWeight: "bold",
              color: "#ff6b6b",
              backgroundColor: "rgba(255, 107, 107, 0.2)",
              padding: "2px 6px",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          />
        )}

        {/* Has kibbles (green) */}
        {showHas && (
          <ScoreLabel
            world={{ x: INFO_WORLD_X, y: INFO_WORLD_Y }}
            text={adultMode ? `$${surplus}` : `${surplus}`}
            style={{
              fontSize: `${infoFont}px`,
              fontWeight: "bold",
              color: "#4cd137",
              backgroundColor: "rgba(76, 209, 55, 0.2)",
              padding: "2px 6px",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          />
        )}

        {/* Budget info */}
        {showBudget && (
          <ScoreLabel
            world={{ x: INFO_WORLD_X, y: INFO_WORLD_Y }}
            text={adultMode ? `$${category.budgeted}` : `${category.budgeted}`}
            style={{
              fontSize: `${infoFont}px`,
              fontWeight: "bold",
              color: "#ffffff",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              padding: "2px 6px",
              borderRadius: "8px",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={className}
      style={{
        position: "relative",
      }}
    >
      {/* Apple Ring around the cat - positioned at actual cat location */}
      <AppleRing
        budgeted={category.budgeted}
        spent={category.spent}
        adultMode={adultMode}
        radius={ringRadius}
        // Apple rings are quite thick
        strokeWidth={ringRadius * 0.22}
        position={{ x: centerX, y: CAT_ACTUAL_Y }}
        opacity={selected ? 1 : 0.5}
        pulse={selected}
      />

      {/* Category Name – top-left badge */}
      <ScoreLabel
        world={{ x: centerX, y: LABEL_WORLD_Y + height - 25 }}
        text={
          category.savings && budgetPercentages?.savingsPercentage !== null
            ? `${category.name} - ${budgetPercentages.savingsPercentage}%`
            : category.name == "Internet & Cell Phone"
              ? "Internet"
              : category.name
        }
        style={{
          fontSize: `${nameFont}px`,
          fontWeight: "bold",
          color: "#ffffff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          padding: "4px 8px",
          borderRadius: "12px",
          whiteSpace: "nowrap",
          maxWidth: `${width - 2 * LABEL_OFFSET}px`,
          lineHeight: `${nameFont * 1.2}px`,
          textAlign: "center",
          transform: "translate(-50%, 0)", // anchor top-center
          opacity: selected || category.spent !== category.budgeted ? 1 : 0.75,
        }}
      />

      {/* Budget info – responsive grid */}
      <InfoLabelGrid adultMode={adultMode} />

      {/* Vertical Budget Bar */}
      <div
        style={{
          position: "absolute",
          left: x + width - 35,
          top: y + 12,
          height: height - 24,
          display: "flex",
          alignItems: "center",
        }}
      >
        <BudgetHealthBar
          budgeted={category.budgeted}
          spent={category.spent}
          adultMode={adultMode}
          selected={selected}
          showHint={showHint}
        />
      </div>

      {/* Helper text for New Car in Liz's level */}
      {isLizLevel && isNewCarCategory && (
        <div
          style={{
            position: "absolute",
            left: x + 8,
            top: y + height + 8,
            width: width - 16, // Match cat element width minus padding
            textAlign: "center",
            zIndex: 10,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "11px",
              color: "#a0aec0",
              fontStyle: "italic",
              lineHeight: 1.2,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              padding: "2px 6px",
              borderRadius: "4px",
              display: "block",
              wordWrap: "break-word",
              whiteSpace: "normal",
              maxWidth: "100%",
            }}
          >
            In 6 months, you will have ${(category.budgeted * 6).toLocaleString()}
          </Typography>
        </div>
      )}

      {/* Debug box outline */}
      {SHOW_DEBUG && (
        <div
          style={{
            position: "absolute",
            left: x,
            top: y,
            width: width,
            height: height,
            border: "3px solid blue",
            backgroundColor: "rgba(0, 0, 255, 0.1)",
            zIndex: 999,
          }}
        />
      )}
    </div>
  );
};

// Enhanced version with interaction states
interface InteractiveCatBoxProps extends CatBoxProps {
  isSelected?: boolean;
  isReceivingTransfer?: boolean;
  onCatClick?: () => void;
}

export const InteractiveCatBox: React.FC<InteractiveCatBoxProps> = ({
  category,
  position,
  adultMode,
  isSelected = false,
  isReceivingTransfer = false,
  onCatClick,
  className,
  catIndex,
  budgetPercentages,
}) => {
  const { x, y, width, height } = position;
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Compute ring dimensions (keep in sync with CatBox calculations)
  const ringRadius = Math.min(width, height, 300) * 0.42;
  const strokeWidth = ringRadius * 0.22;
  const outerR = ringRadius + strokeWidth / 2;

  // State for hover feedback
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      className={className}
      style={{
        position: "relative",
        cursor: "default",
      }}
    >
      {/* Interactive overlay covering the entire cell area */}
      {typeof catIndex === "number" && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            zIndex: 50,
            background: SHOW_DEBUG
              ? isHovered
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 255, 0, 0.05)"
              : "transparent",
            border: SHOW_DEBUG ? "3px solid lime" : "none",
            borderRadius: "8px",
            transition: "all 0.2s ease-in-out",
            boxShadow: isHovered
              ? "0 0 20px rgba(255, 255, 255, 0.3)"
              : isSelected
                ? "0 0 15px rgba(0, 255, 0, 0.6)"
                : "0 0 5px rgba(0, 255, 0, 0.3)",
          }}
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => {
            setIsHovered(false);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const scene = (window as any).PhaserSceneReact;
            scene?.handleCatClick(catIndex);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const scene = (window as any).PhaserSceneReact;
            scene?.handleCatClick(catIndex);
          }}
        />
      )}

      <CatBox
        category={category}
        position={position}
        adultMode={adultMode}
        selected={isSelected}
        budgetPercentages={budgetPercentages}
      />

      {/* Selection indicator */}
      {isSelected && (
        <ScoreLabel
          world={{ x: centerX, y: centerY - 120 }}
          text="✨ Selected"
          style={{
            fontSize: "16px",
            color: "#FFD700",
            fontWeight: "bold",
            textAlign: "center",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            animation: "pulse 1.5s ease-in-out infinite",
          }}
        />
      )}

      {/* Hover hint for better UX */}
      {isHovered && !isSelected && (
        <ScoreLabel
          world={{ x: centerX, y: centerY - 120 }}
          text="👆 Click to select"
          style={{
            fontSize: "14px",
            color: "#FFFFFF",
            fontWeight: "bold",
            textAlign: "center",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            padding: "4px 8px",
            borderRadius: "8px",
          }}
        />
      )}

      {/* Transfer animation */}
      {isReceivingTransfer && (
        <ScoreLabel
          world={{ x: centerX, y: centerY - 140 }}
          text="💰 Receiving..."
          style={{
            fontSize: "14px",
            color: "#4ECDC4",
            fontWeight: "bold",
            textAlign: "center",
            textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
            animation: "bounce 1s ease-in-out infinite",
          }}
        />
      )}
    </div>
  );
};
